import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, Users, Check, X, Settings, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parseISO, addDays, startOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = { '1': 'Lunedì', '2': 'Martedì', '3': 'Mercoledì', '4': 'Giovedì', '5': 'Venerdì', '6': 'Sabato', '0': 'Domenica' };
const DAY_ORDER = ['1', '2', '3', '4', '5', '6', '0'];
const ALL_HOURS = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00'];

const AdminLessonsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('bookings');
  const [saving, setSaving] = useState(false);

  // Settings
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [dateOverrides, setDateOverrides] = useState({});
  const [editingDay, setEditingDay] = useState(null); // which weekly day is being edited
  const [editingDate, setEditingDate] = useState(null); // which specific date override is being edited
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [bookRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/lessons`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/lessons/settings`, { withCredentials: true })
      ]);
      setBookings(bookRes.data);
      setSettings(settingsRes.data);
      setWeeklySchedule(settingsRes.data.weekly_schedule || {});
      setDateOverrides(settingsRes.data.date_overrides || {});
    } catch (error) {
      if (error.response?.status === 403) navigate('/dashboard');
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/lessons/${bookingId}/status`, { status }, { withCredentials: true });
      toast.success(status === 'confirmed' ? 'Lezione confermata!' : 'Lezione rifiutata');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/lessons/settings`, {
        enabled: true,
        weekly_schedule: weeklySchedule,
        date_overrides: dateOverrides,
      }, { withCredentials: true });
      toast.success('Impostazioni salvate!');
      setEditingDay(null);
      setEditingDate(null);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const toggleWeeklyHour = (day, hour) => {
    setWeeklySchedule(prev => {
      const dayHours = prev[day] || [];
      const updated = dayHours.includes(hour)
        ? dayHours.filter(h => h !== hour)
        : [...dayHours, hour].sort();
      return { ...prev, [day]: updated };
    });
  };

  const toggleAllHoursForDay = (day) => {
    setWeeklySchedule(prev => {
      const current = prev[day] || [];
      const updated = current.length > 0 ? [] : ALL_HOURS.slice();
      return { ...prev, [day]: updated };
    });
  };

  const addDateOverride = (date) => {
    if (!date) return;
    const dateStr = format(date, 'yyyy-MM-dd');
    if (!dateOverrides[dateStr]) {
      setDateOverrides(prev => ({ ...prev, [dateStr]: [] }));
      setEditingDate(dateStr);
    }
    setCalendarOpen(false);
  };

  const toggleOverrideHour = (dateStr, hour) => {
    setDateOverrides(prev => {
      const hours = prev[dateStr] || [];
      const updated = hours.includes(hour)
        ? hours.filter(h => h !== hour)
        : [...hours, hour].sort();
      return { ...prev, [dateStr]: updated };
    });
  };

  const removeOverride = (dateStr) => {
    setDateOverrides(prev => {
      const copy = { ...prev };
      delete copy[dateStr];
      return copy;
    });
    if (editingDate === dateStr) setEditingDate(null);
  };

  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-slate-400">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="admin-lessons-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-outfit text-2xl font-semibold text-slate-900">Gestione Lezioni</h1>
          <p className="text-slate-500 text-sm">Prenotazioni e disponibilità</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl p-1.5 border border-slate-100 grid grid-cols-2 gap-1.5 mb-6">
        <button
          onClick={() => setTab('bookings')}
          data-testid="tab-bookings"
          className={`py-2.5 rounded-xl text-sm font-semibold transition-all relative ${
            tab === 'bookings' ? 'bg-emerald-400 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Prenotazioni
          {pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {pendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('settings')}
          data-testid="tab-settings"
          className={`py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
            tab === 'settings' ? 'bg-emerald-400 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Settings className="w-4 h-4" />
          Disponibilità
        </button>
      </div>

      {/* ========== BOOKINGS TAB ========== */}
      {tab === 'bookings' && (
        <div className="space-y-3">
          {bookings.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
              <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">Nessuna prenotazione</p>
            </div>
          ) : (
            bookings.map(b => (
              <div key={b.booking_id} data-testid={`admin-booking-${b.booking_id}`}
                className={`bg-white rounded-2xl p-4 border transition-all ${
                  b.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        b.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {b.status === 'pending' ? 'Da confermare' : 'Confermata'}
                      </span>
                      <span className="text-xs text-slate-400">{b.label} · {b.price}€</span>
                    </div>
                    <p className="font-medium text-slate-900">{b.player_name}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-sm text-slate-500">
                      <span className="flex items-center gap-1 capitalize">
                        <Calendar className="w-3.5 h-3.5" />
                        {format(parseISO(b.date), 'EEE d MMM', { locale: it })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {b.time}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {b.num_players}
                      </span>
                    </div>
                  </div>
                  {b.status === 'pending' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => updateBookingStatus(b.booking_id, 'confirmed')} data-testid={`confirm-${b.booking_id}`}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors">
                        <Check className="w-5 h-5" />
                      </button>
                      <button onClick={() => updateBookingStatus(b.booking_id, 'rejected')} data-testid={`reject-${b.booking_id}`}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-100 text-red-500 hover:bg-red-200 transition-colors">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ========== SETTINGS TAB ========== */}
      {tab === 'settings' && (
        <div className="space-y-5">
          {/* Prices */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-medium text-slate-900 mb-3">Prezzi</h3>
            <div className="grid grid-cols-4 gap-2">
              {[
                { n: '1', label: 'Singola', price: settings?.prices?.['1'] || 30 },
                { n: '2', label: 'Doppia', price: settings?.prices?.['2'] || 40 },
                { n: '3', label: 'Tripla', price: settings?.prices?.['3'] || 45 },
                { n: '4', label: 'In 4', price: settings?.prices?.['4'] || 60 },
              ].map(p => (
                <div key={p.n} className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <span className="block text-lg font-bold text-slate-900">{p.price}€</span>
                  <span className="text-xs text-slate-500">{p.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-medium text-slate-900 mb-1 flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-500" />
              Orario Settimanale
            </h3>
            <p className="text-xs text-slate-400 mb-4">Tocca un giorno per modificare gli orari</p>

            <div className="space-y-2">
              {DAY_ORDER.map(day => {
                const hours = weeklySchedule[day] || [];
                const isEditing = editingDay === day;
                const isClosed = hours.length === 0;

                return (
                  <div key={day} className="rounded-xl border border-slate-100 overflow-hidden">
                    <button
                      onClick={() => setEditingDay(isEditing ? null : day)}
                      data-testid={`weekly-day-${day}`}
                      className={`w-full flex items-center justify-between p-3 transition-colors ${
                        isEditing ? 'bg-emerald-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${isClosed ? 'bg-red-400' : 'bg-emerald-400'}`} />
                        <span className="text-sm font-medium text-slate-900">{DAY_NAMES[day]}</span>
                      </div>
                      <span className="text-xs text-slate-400">
                        {isClosed ? 'Chiuso' : `${hours.length} orari · ${hours[0]} - ${hours[hours.length - 1]}`}
                      </span>
                    </button>

                    {isEditing && (
                      <div className="px-3 pb-3 pt-1" data-testid={`weekly-hours-${day}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-500">Seleziona gli orari disponibili:</span>
                          <button
                            onClick={() => toggleAllHoursForDay(day)}
                            className="text-xs font-medium text-sky-500 hover:text-sky-600"
                          >
                            {isClosed ? 'Apri tutto' : 'Chiudi tutto'}
                          </button>
                        </div>
                        <div className="grid grid-cols-5 gap-1.5">
                          {ALL_HOURS.map(hour => (
                            <button
                              key={hour}
                              onClick={() => toggleWeeklyHour(day, hour)}
                              data-testid={`weekly-${day}-${hour}`}
                              className={`py-2 rounded-lg text-xs font-medium transition-all ${
                                hours.includes(hour)
                                  ? 'bg-emerald-400 text-white'
                                  : 'bg-slate-100 text-slate-400'
                              }`}
                            >
                              {hour}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Date Overrides */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-medium text-slate-900 mb-1 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-amber-500" />
              Eccezioni per Giorni Specifici
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              Sovrascrivi l'orario settimanale per date specifiche (ferie, imprevisti, ecc.)
            </p>

            {Object.keys(dateOverrides).sort().map(dateStr => {
              const hours = dateOverrides[dateStr];
              const isEditing = editingDate === dateStr;
              const isClosed = hours.length === 0;

              return (
                <div key={dateStr} className="rounded-xl border border-amber-200 bg-amber-50/30 overflow-hidden mb-2">
                  <div className="flex items-center justify-between p-3">
                    <button
                      onClick={() => setEditingDate(isEditing ? null : dateStr)}
                      className="flex items-center gap-3 flex-1"
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${isClosed ? 'bg-red-400' : 'bg-amber-400'}`} />
                      <span className="text-sm font-medium text-slate-900 capitalize">
                        {format(parseISO(dateStr), 'EEEE d MMMM', { locale: it })}
                      </span>
                      <span className="text-xs text-slate-400">
                        {isClosed ? 'Chiuso' : `${hours.length} orari`}
                      </span>
                    </button>
                    <button
                      onClick={() => removeOverride(dateStr)}
                      data-testid={`remove-override-${dateStr}`}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-red-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {isEditing && (
                    <div className="px-3 pb-3" data-testid={`override-hours-${dateStr}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-500">Orari per questa data:</span>
                        <button
                          onClick={() => setDateOverrides(prev => ({
                            ...prev,
                            [dateStr]: prev[dateStr].length > 0 ? [] : ALL_HOURS.slice()
                          }))}
                          className="text-xs font-medium text-sky-500 hover:text-sky-600"
                        >
                          {isClosed ? 'Apri tutto' : 'Chiudi tutto'}
                        </button>
                      </div>
                      <div className="grid grid-cols-5 gap-1.5">
                        {ALL_HOURS.map(hour => (
                          <button
                            key={hour}
                            onClick={() => toggleOverrideHour(dateStr, hour)}
                            className={`py-2 rounded-lg text-xs font-medium transition-all ${
                              hours.includes(hour)
                                ? 'bg-amber-400 text-white'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {hour}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="add-override-date-btn"
                  className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 hover:border-amber-300 hover:text-amber-600 transition-colors text-sm mt-2"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi eccezione per una data
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  onSelect={addDateOverride}
                  disabled={(date) => date < new Date()}
                  locale={it}
                  className="rounded-2xl border-0"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Save */}
          <button
            onClick={saveSettings}
            disabled={saving}
            data-testid="save-lesson-settings-btn"
            className="w-full py-4 bg-emerald-500 text-white rounded-full font-medium text-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Salva Impostazioni
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminLessonsPage;
